import { COUNTRY_DIAL_CODES, DEFAULT_COUNTRY_ISO2 } from './countryDialCodes'

export const E164_REG_EXP = /^\+[1-9]\d{7,14}$/

const BY_ISO2 = COUNTRY_DIAL_CODES.reduce((acc, [iso2, dialCode, name]) => {
  acc[iso2] = { iso2, dialCode, name }
  return acc
}, {})

const BY_DIAL_CODE = COUNTRY_DIAL_CODES.reduce((acc, [iso2, dialCode]) => {
  if (!acc[dialCode]) acc[dialCode] = iso2
  return acc
}, {})

const DIAL_CODES_LONGEST_FIRST = Object.keys(BY_DIAL_CODE).sort(
  (a, b) => b.length - a.length,
)

const nameKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const BY_NAME = COUNTRY_DIAL_CODES.reduce((acc, [iso2, , ...names]) => {
  ;[iso2, ...names].forEach((name) => {
    if (!acc[nameKey(name)]) acc[nameKey(name)] = iso2
  })
  return acc
}, {})

/**
 * Resolve an ISO2 code or a country name to an ISO2 code.
 *
 * Lets callers hand over whatever they already hold — a position's `country`
 * field, say — without knowing which form it is in.
 */
export const resolveCountryIso2 = (
  country,
  fallback = DEFAULT_COUNTRY_ISO2,
) => {
  const asIso2 = String(country ?? '')
    .trim()
    .toUpperCase()
  if (BY_ISO2[asIso2]) return asIso2
  return BY_NAME[nameKey(country)] || fallback
}

const ISO2_TO_REGIONAL_INDICATOR = 0x1f1e6 - 'A'.charCodeAt(0)

/** Flag emoji for an ISO2 code. Unassigned codes (e.g. XK) fall back to the letters. */
export const countryFlag = (iso2) =>
  String.fromCodePoint(
    ...[...(iso2 || '')].map(
      (char) => char.charCodeAt(0) + ISO2_TO_REGIONAL_INDICATOR,
    ),
  )

export const dialCodeFor = (iso2) => BY_ISO2[iso2]?.dialCode || ''

export const countryNameFor = (iso2) => BY_ISO2[iso2]?.name || ''

/**
 * Options for the country picker, ordered by country name.
 *
 * `label` is the compact form for the closed control, `title` the full form for
 * the open list. `searchText` carries the aliases too, so 'UAE', 'Emirates' and
 * '+971' all reach the same entry.
 */
export const countryDialOptions = COUNTRY_DIAL_CODES.map(
  ([iso2, dialCode, ...names]) => ({
    value: iso2,
    label: `${countryFlag(iso2)} +${dialCode}`,
    title: `${names[0]} (+${dialCode})`,
    flag: countryFlag(iso2),
    name: names[0],
    dialCode,
    searchText: [...names, iso2, dialCode, `+${dialCode}`]
      .join(' ')
      .toLowerCase(),
  }),
).sort((a, b) => a.name.localeCompare(b.name))

/**
 * Filter the picker's options by a typed query.
 *
 * Matches country name, alias, ISO2 code or dial code, so both 'eg' and '20'
 * reach Egypt. Name matches are floated above mid-string ones, so typing 'ind'
 * offers India before British Indian Ocean Territory.
 *
 * @returns {Array} every option when the query is blank
 */
export const searchCountryDialOptions = (query) => {
  const needle = String(query ?? '')
    .trim()
    .toLowerCase()
    .replace(/^\+/, '')
  if (!needle) return countryDialOptions
  return countryDialOptions
    .filter((option) => option.searchText.includes(needle))
    .sort(
      (a, b) =>
        Number(b.name.toLowerCase().startsWith(needle)) -
        Number(a.name.toLowerCase().startsWith(needle)),
    )
}

const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '')

const stripTrunkPrefix = (digits) =>
  digits.length > 1 && digits.startsWith('0') ? digits.slice(1) : digits

const matchLongestDialCode = (digits) =>
  DIAL_CODES_LONGEST_FIRST.find((dialCode) => digits.startsWith(dialCode))

/**
 * Split a stored value into the parts the picker and the text input each hold.
 *
 * Only a value carrying its own country code is attributed to a country; anything
 * else is treated as a national number against `fallbackIso2` rather than guessed
 * at, so an existing record never silently changes country on being opened.
 *
 * @returns {{countryIso2: string, nationalNumber: string}}
 */
export const splitPhone = (value, fallbackIso2 = DEFAULT_COUNTRY_ISO2) => {
  const raw = String(value ?? '').trim()
  const digits = digitsOnly(raw)
  if (!digits) return { countryIso2: fallbackIso2, nationalNumber: '' }

  const isInternational = raw.startsWith('+') || digits.startsWith('00')
  if (!isInternational) {
    return {
      countryIso2: fallbackIso2,
      nationalNumber: stripTrunkPrefix(digits),
    }
  }

  const international = digits.startsWith('00') ? digits.slice(2) : digits
  const dialCode = matchLongestDialCode(international)
  if (!dialCode)
    return { countryIso2: fallbackIso2, nationalNumber: international }
  return {
    countryIso2: BY_DIAL_CODE[dialCode],
    nationalNumber: international.slice(dialCode.length),
  }
}

/**
 * Join a picked country and a typed national number into E.164.
 *
 * @returns {string} the joined number, or '' when there is nothing to join. The
 *   result is not necessarily valid yet — it is built while the user is still
 *   typing, and `isValidPhone` is what the schema checks.
 */
export const joinPhone = (iso2, nationalNumber) => {
  const national = stripTrunkPrefix(digitsOnly(nationalNumber))
  const dialCode = dialCodeFor(iso2)
  if (!national || !dialCode) return ''
  return `+${dialCode}${national}`
}

export const isValidPhone = (value) => E164_REG_EXP.test(String(value ?? ''))

export { DEFAULT_COUNTRY_ISO2 }
